import cn from 'classnames';
import NeedsSelectorCommon from 'components/pet-profile/needs-selector';
import { PetType } from 'constants/pet-type';
import { NutritionSegmentationIds } from 'constants/segmentation';
import React, { memo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useClickAway } from 'react-use';
import { useNeedCapture } from '../../../../state';
import AnimateDropdown from '../animate-dropdown';
import SelectorButton from '../selector-button';

interface NeedsSelectorProps {
  isMobile: boolean;
  petType: 'dog' | 'cat';
}
/**
 * Needs Selector component of the NeedCapture component
 */
const NeedsSelector: React.FC<NeedsSelectorProps> = ({ isMobile, petType }) => {
  const {
    activePetType,
    draftData,
    state,
    dispatch,
    handleConfirmNeeds,
    handleSelectAllergies,
    handleSelectNeeds,
  } = useNeedCapture();
  const { draftNeeds, draftAllergies } = draftData;
  const needsSelectorRef = useRef<HTMLDivElement>(null);

  useClickAway(needsSelectorRef, () => {
    if (!isMobile && state.isNeedSelectorOpen) handleConfirmNeeds();
  });

  return (
    <div className="gap flex items-center gap-3 lg:grow">
      <div
        className={cn(
          'flex w-full items-center gap-3 lg:block',
          activePetType === PetType.Dog ? 'w-full' : 'lg:max-w-70'
        )}
      >
        {petType === 'dog' && (
          <span className="hidden shrink-0">
            <FormattedMessage id="landingpage:need-capture:needs:text:desktop" />
          </span>
        )}
        <div
          className="relative flex w-full items-center lg:block"
          ref={needsSelectorRef}
        >
          <SelectorButton
            isOpen={state.isNeedSelectorOpen}
            onClick={() =>
              state.isNeedSelectorOpen
                ? handleConfirmNeeds()
                : dispatch({ type: 'TOGGLE_NEED_SELECTOR' })
            }
          >
            {draftNeeds && draftNeeds.length > 0 ? (
              <div className="line-clamp-1 text-left">
                {draftNeeds.map((need, index) => (
                  <span key={need}>
                    <FormattedMessage
                      id={`landingpage:need-capture:need:option:${need}`}
                    />
                    {need === NutritionSegmentationIds.Intolerance &&
                      draftAllergies &&
                      draftAllergies.length > 0 && (
                        <>
                          {': '}
                          {draftAllergies.map((allergy) => (
                            <span key={allergy}>
                              <FormattedMessage
                                id={`common:allergy:${allergy}`}
                              />
                              {draftAllergies.indexOf(allergy) <
                                draftAllergies.length - 1 && ', '}
                            </span>
                          ))}
                        </>
                      )}
                    {draftNeeds && index < draftNeeds.length - 1 && ', '}
                  </span>
                ))}
              </div>
            ) : (
              <span className="italic">
                <FormattedMessage id="landingpage:need-capture:needs:placeholder" />
              </span>
            )}
          </SelectorButton>

          {/* Desktop Dropdown */}
          {!isMobile && (
            <AnimateDropdown
              className="!max-h-120 lg:min-w-142"
              isOpen={state.isNeedSelectorOpen}
              hasButtons
              onBack={() => handleConfirmNeeds()}
              onConfirm={() => handleConfirmNeeds()}
            >
              <NeedsSelectorCommon
                className="mx-4"
                selectedOption={draftData}
                onSelectAllergies={(value) => handleSelectAllergies(value)}
                onSelectNeeds={handleSelectNeeds}
                petType={activePetType ?? PetType.Dog}
              />
            </AnimateDropdown>
          )}
        </div>
        {petType === 'cat' && (
          <span className="block shrink-0 lg:hidden">
            <FormattedMessage
              id={`landingpage:need-capture:age:text:cat:separator-2`}
            />
          </span>
        )}
      </div>
      {petType === 'cat' && (
        <span className="hidden shrink-0 lg:block">
          <FormattedMessage
            id={`landingpage:need-capture:age:text:cat:separator-4`}
          />
        </span>
      )}
    </div>
  );
};

export default memo(NeedsSelector);
